import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { FolderTree, Layers, Plus, Search, X } from 'lucide-react';
import {
  formatMoney,
  formatQty,
  isValidGtin,
  UNITS,
  UNIT_KEYS,
  unitAllowsDecimals,
  VAT_CATEGORIES,
  VAT_CATEGORY_KEYS,
  type CurrencyCode,
} from '@tengesa/shared';
import { api } from '../../lib/api';
import type { Category, ProductDto } from '../../lib/types';
import { useSession } from '../../app/session';
import { useCurrentBranch } from '../../app/Shell';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  EmptyState,
  ErrorNotice,
  Field,
  FormError,
  PageHeader,
  Panel,
  Select,
  Spinner,
  Switch,
  TextArea,
  TextInput,
  fieldErrors,
  useToast,
} from '../../ui';
import { MoneyInput, QtyInput, useDebounced } from '../../ui/inputs';
import { StockHistoryDialog } from '../stock/StockHistoryDialog';

const PAGE = 50;

export function ProductsPage() {
  const { me, can, hasModule } = useSession();
  const { branch } = useCurrentBranch();
  const cur = me.business.baseCurrency as CurrencyCode;
  const [q, setQ] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [stock, setStock] = useState('');
  const [active, setActive] = useState<'true' | 'false'>('true');
  const [page, setPage] = useState(0);
  const [editing, setEditing] = useState<ProductDto | 'new' | null>(null);
  const [history, setHistory] = useState<ProductDto | null>(null);
  const [catsOpen, setCatsOpen] = useState(false);
  const [newGroup, setNewGroup] = useState(false);
  const [options, setOptions] = useState<ProductDto | null>(null);
  const dq = useDebounced(q.trim(), 200);

  const cats = useQuery({ queryKey: ['categories'], queryFn: () => api.get<Category[]>('/api/categories') });
  const res = useQuery({
    queryKey: ['products', dq, categoryId, stock, active, branch?.id, page],
    queryFn: () => {
      const p = new URLSearchParams({ limit: String(PAGE), offset: String(page * PAGE), active });
      if (dq) p.set('q', dq);
      if (categoryId) p.set('categoryId', categoryId);
      if (stock) p.set('stock', stock);
      if (branch) p.set('branchId', branch.id);
      return api.get<{ items: ProductDto[]; total: number }>(`/api/products?${p}`);
    },
  });
  const seeCost = can('products.view_cost');

  return (
    <>
      <PageHeader
        title="Products"
        description={`Prices, codes and stock at ${branch?.name ?? 'your branch'}.`}
        actions={
          can('products.manage') && (
            <>
              <Button icon={<FolderTree className="size-4" />} onClick={() => setCatsOpen(true)}>Categories</Button>
              {hasModule('variants') && can('variants.manage') && (
                <Button icon={<Layers className="size-4" />} onClick={() => setNewGroup(true)}>Product with options</Button>
              )}
              <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setEditing('new')}>Add product</Button>
            </>
          )
        }
      />
      <Panel>
        <div className="grid gap-3 border-b border-line p-4 sm:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr]">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" />
            <TextInput aria-label="Search products" placeholder="Name, code or barcode" className="pl-9" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} />
          </div>
          <Select aria-label="Category" value={categoryId} onChange={(e) => { setCategoryId(e.target.value); setPage(0); }}>
            <option value="">All categories</option>
            {cats.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
          <Select aria-label="Stock level" value={stock} onChange={(e) => { setStock(e.target.value); setPage(0); }}>
            <option value="">Any stock level</option>
            <option value="low">At or below reorder level</option>
            <option value="out">Out of stock</option>
          </Select>
          <Select aria-label="Active" value={active} onChange={(e) => { setActive(e.target.value as 'true' | 'false'); setPage(0); }}>
            <option value="true">Products on sale</option>
            <option value="false">Discontinued</option>
          </Select>
        </div>
        {res.isLoading && <Spinner />}
        {res.error && <div className="p-4"><ErrorNotice error={res.error} onRetry={() => res.refetch()} /></div>}
        {res.data && res.data.items.length === 0 && (
          <EmptyState title="No products found" action={can('products.manage') && !dq ? <Button variant="primary" onClick={() => setEditing('new')}>Add product</Button> : undefined}>
            {dq ? 'Check the spelling, or search by barcode.' : 'Add the products you sell.'}
          </EmptyState>
        )}
        {res.data && res.data.items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left">
              <thead>
                <tr className="border-b border-line text-sm text-muted">
                  <th className="px-5 py-2.5 font-medium">Product</th>
                  <th className="px-3 py-2.5 font-medium">Category</th>
                  <th className="px-3 py-2.5 text-right font-medium">Price</th>
                  {seeCost && <th className="px-3 py-2.5 text-right font-medium">Avg cost</th>}
                  {seeCost && <th className="px-3 py-2.5 text-right font-medium">Margin</th>}
                  <th className="px-5 py-2.5 text-right font-medium">In stock</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {res.data.items.map((p) => {
                  const cost = p.stock?.avgCostCents ?? p.costCents ?? 0;
                  const margin = p.priceCents > 0 && seeCost ? Math.round(((p.priceCents - cost) / p.priceCents) * 1000) / 10 : null;
                  const qty = p.stock?.qtyMilli ?? 0;
                  const low = p.trackStock && qty <= p.reorderLevelMilli;
                  return (
                    <tr key={p.id} className="hover:bg-ledger-50/60">
                      <td className="px-5 py-2.5">
                        <button className="text-left font-medium hover:underline" onClick={() => (p.isVariantParent ? setOptions(p) : can('products.manage') ? setEditing(p) : setHistory(p))}>
                          {p.name}
                        </button>
                        <span className="block text-xs text-muted">
                          {p.sku}
                          {p.barcodes[0] ? ` — ${p.barcodes[0]}` : ''}
                          {p.vatCategory !== 'standard' && ` — ${VAT_CATEGORIES[p.vatCategory]}`}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-sm">{p.categoryName ?? '—'}</td>
                      <td className="px-3 py-2.5 text-right num">
                        {p.isVariantParent ? <span className="text-sm text-muted">Per option</span> : formatMoney(p.priceCents, cur)}
                        {unitAllowsDecimals(p.unit) && <span className="text-xs text-muted">/{p.unit}</span>}
                      </td>
                      {seeCost && <td className="px-3 py-2.5 text-right text-sm num">{formatMoney(cost, cur)}</td>}
                      {seeCost && <td className={clsx('px-3 py-2.5 text-right text-sm num', margin != null && margin < 10 && 'text-warn')}>{margin != null ? `${margin}%` : '—'}</td>}
                      <td className="px-5 py-2.5 text-right">
                        {p.isVariantParent ? (
                          <button onClick={() => setOptions(p)} className="text-sm font-medium text-ledger-700 hover:underline">
                            {Object.keys(p.variantAttributes ?? {}).join(' and ')} options
                          </button>
                        ) : p.trackStock ? (
                          <button onClick={() => setHistory(p)} className={clsx('num hover:underline', qty <= 0 ? 'text-danger' : low ? 'text-warn' : '')}>
                            {formatQty(qty)} {UNITS[p.unit as keyof typeof UNITS]?.short}
                          </button>
                        ) : (
                          <span className="text-sm text-muted">Not stocked</span>
                        )}
                        {p.trackStock && low && <span className="ml-2"><Badge tone={qty <= 0 ? 'danger' : 'warn'}>{qty <= 0 ? 'Out' : 'Low'}</Badge></span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {res.data && res.data.total > PAGE && (
          <div className="flex items-center justify-between border-t border-line px-5 py-3">
            <Button size="sm" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</Button>
            <span className="text-sm text-muted">{res.data.total} products — page {page + 1}</span>
            <Button size="sm" disabled={(page + 1) * PAGE >= res.data.total} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        )}
      </Panel>
      {editing && <ProductDialog product={editing === 'new' ? null : editing} categories={cats.data ?? []} onClose={() => setEditing(null)} />}
      {history && branch && <StockHistoryDialog product={history} branchId={branch.id} onClose={() => setHistory(null)} />}
      {catsOpen && <CategoriesDialog categories={cats.data ?? []} onClose={() => setCatsOpen(false)} />}
      {newGroup && <VariantGroupDialog categories={cats.data ?? []} onClose={() => setNewGroup(false)} onCreated={(p) => { setNewGroup(false); setOptions(p); }} />}
      {options && <VariantsDialog parent={options} branchId={branch?.id} onClose={() => setOptions(null)} />}
    </>
  );
}

function ProductDialog({ product, categories, onClose }: { product: ProductDto | null; categories: Category[]; onClose: () => void }) {
  const { me, hasModule, can } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const qc = useQueryClient();
  const toast = useToast();
  const [v, setV] = useState({
    sku: product?.sku ?? '',
    name: product?.name ?? '',
    description: product?.description ?? '',
    categoryId: product?.categoryId ?? '',
    unit: product?.unit ?? 'each',
    priceCents: product?.priceCents ?? null as number | null,
    costCents: product?.costCents ?? null as number | null,
    vatCategory: product?.vatCategory ?? 'standard',
    reorderLevelMilli: product?.reorderLevelMilli ?? 0 as number | null,
    trackStock: product?.trackStock ?? true,
    isActive: product?.isActive ?? true,
    hsCode: product?.hsCode ?? '',
    barcodes: product?.barcodes ?? [],
    tracksExpiry: product?.tracksExpiry ?? false,
    pluCode: product?.pluCode ?? '',
  });
  const [newCode, setNewCode] = useState('');
  const set = <K extends keyof typeof v>(k: K, val: (typeof v)[K]) => setV((p) => ({ ...p, [k]: val }));
  const m = useMutation({
    mutationFn: () => {
      const body = {
        ...v,
        categoryId: v.categoryId || null,
        priceCents: v.priceCents ?? -1,
        costCents: v.costCents ?? 0,
        reorderLevelMilli: v.reorderLevelMilli ?? 0,
      };
      return product ? api.put<ProductDto>(`/api/products/${product.id}`, body) : api.post<ProductDto>('/api/products', body);
    },
    onSuccess: () => {
      toast(product ? 'Product saved' : 'Product added');
      void qc.invalidateQueries({ queryKey: ['products'] });
      void qc.invalidateQueries({ queryKey: ['categories'] });
      onClose();
    },
  });
  const f = fieldErrors(m.error);
  const addCode = () => {
    const c = newCode.trim();
    if (c && !v.barcodes.includes(c)) set('barcodes', [...v.barcodes, c]);
    setNewCode('');
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    m.mutate();
  };
  const margin = v.priceCents && v.costCents != null ? Math.round(((v.priceCents - v.costCents) / v.priceCents) * 1000) / 10 : null;
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={product ? product.name : 'Add product'}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="product-form" busy={m.isPending}>{product ? 'Save product' : 'Add product'}</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <form id="product-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-6" noValidate>
        <Field label="Name" htmlFor="p-name" error={f.name} className="sm:col-span-4">
          <TextInput id="p-name" value={v.name} onChange={(e) => set('name', e.target.value)} invalid={!!f.name} />
        </Field>
        <Field label="Product code (SKU)" htmlFor="p-sku" error={f.sku} className="sm:col-span-2">
          <TextInput id="p-sku" value={v.sku} onChange={(e) => set('sku', e.target.value.toUpperCase())} invalid={!!f.sku} />
        </Field>
        <Field label="Category" htmlFor="p-cat" error={f.categoryId} className="sm:col-span-2">
          <Select id="p-cat" value={v.categoryId} onChange={(e) => set('categoryId', e.target.value)}>
            <option value="">None</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </Field>
        <Field label="Sold by" htmlFor="p-unit" error={f.unit} className="sm:col-span-2">
          <Select id="p-unit" value={v.unit} onChange={(e) => set('unit', e.target.value)}>
            {UNIT_KEYS.map((u) => <option key={u} value={u}>{UNITS[u].label}{UNITS[u].decimals ? ' (weighed/measured)' : ''}</option>)}
          </Select>
        </Field>
        <Field label="VAT" htmlFor="p-vat" error={f.vatCategory} className="sm:col-span-2">
          <Select id="p-vat" value={v.vatCategory} onChange={(e) => set('vatCategory', e.target.value as typeof v.vatCategory)}>
            {VAT_CATEGORY_KEYS.map((k) => <option key={k} value={k}>{VAT_CATEGORIES[k]}</option>)}
          </Select>
        </Field>
        <Field label={`Selling price${unitAllowsDecimals(v.unit) ? ` per ${v.unit}` : ''} (incl. VAT)`} htmlFor="p-price" error={f.priceCents} className="sm:col-span-2">
          <MoneyInput id="p-price" currency={cur} value={v.priceCents} onChange={(c) => set('priceCents', c)} invalid={!!f.priceCents} />
        </Field>
        {can('products.view_cost') && (
          <Field label="Latest cost price" htmlFor="p-cost" error={f.costCents} className="sm:col-span-2" hint={margin != null ? `Margin ${margin}%` : me.business.vatRegistered ? 'Excluding VAT you can claim back' : undefined}>
            <MoneyInput id="p-cost" currency={cur} value={v.costCents} onChange={(c) => set('costCents', c)} />
          </Field>
        )}
        <Field label="Reorder when stock reaches" htmlFor="p-reorder" error={f.reorderLevelMilli} className="sm:col-span-2">
          <QtyInput id="p-reorder" unit={v.unit} value={v.reorderLevelMilli} onChange={(q) => set('reorderLevelMilli', q)} allowZero />
        </Field>
        <div className="sm:col-span-6">
          <p className="mb-1.5 text-sm font-medium">Barcodes</p>
          <div className="flex flex-wrap items-center gap-2">
            {v.barcodes.map((c) => (
              <span key={c} className="inline-flex items-center gap-1 rounded-full bg-paper py-1 pl-3 pr-1 text-sm num">
                {c}
                {/^\d{8}$|^\d{12,14}$/.test(c) && !isValidGtin(c) && <span className="text-warn" title="Check digit doesn't match">?</span>}
                <button type="button" aria-label={`Remove ${c}`} onClick={() => set('barcodes', v.barcodes.filter((x) => x !== c))} className="rounded-full p-1 hover:bg-line">
                  <X className="size-3" />
                </button>
              </span>
            ))}
            <TextInput
              aria-label="Add barcode"
              className="max-w-56"
              placeholder="Scan or type, then Enter"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCode();
                }
              }}
              onBlur={addCode}
            />
          </div>
          {f.barcodes && <p className="mt-1 text-sm text-danger">{f.barcodes}</p>}
        </div>
        {hasModule('weighing') && unitAllowsDecimals(v.unit) && (
          <Field label="Scale PLU code" htmlFor="p-plu" error={f.pluCode} className="sm:col-span-2" hint="Number the scale prints in its barcode">
            <TextInput id="p-plu" inputMode="numeric" value={v.pluCode} onChange={(e) => set('pluCode', e.target.value.replace(/\D/g, ''))} />
          </Field>
        )}
        <Field label="HS code (optional)" htmlFor="p-hs" error={f.hsCode} className="sm:col-span-2" hint="Needed for ZIMRA fiscal receipts">
          <TextInput id="p-hs" inputMode="numeric" value={v.hsCode} onChange={(e) => set('hsCode', e.target.value.replace(/\D/g, ''))} />
        </Field>
        <Field label="Description (optional)" htmlFor="p-desc" className="sm:col-span-6">
          <TextArea id="p-desc" rows={2} value={v.description} onChange={(e) => set('description', e.target.value)} />
        </Field>
        <div className="space-y-3 sm:col-span-6">
          <Checkbox label="Keep stock counts for this product" description="Untick for services such as delivery charges." checked={v.trackStock} onChange={(x) => set('trackStock', x)} />
          {hasModule('expiry') && v.trackStock && (
            <Checkbox label="Track batches and expiry dates" description="Expiry dates are required when receiving, and the earliest-expiring stock is sold first." checked={v.tracksExpiry} onChange={(x) => set('tracksExpiry', x)} />
          )}
          {f.unit && <p className="text-sm text-danger">{f.unit}</p>}
          {f.tracksExpiry && <p className="text-sm text-danger">{f.tracksExpiry}</p>}
        </div>
        {product && (
          <div className="flex items-center justify-between gap-4 rounded-[8px] bg-paper px-4 py-3 sm:col-span-6">
            <div>
              <p className="font-medium">On sale</p>
              <p className="text-sm text-muted">Discontinued products can't be sold. Their history is kept.</p>
            </div>
            <Switch checked={v.isActive} onChange={(x) => set('isActive', x)} label="On sale" />
          </div>
        )}
      </form>
    </Dialog>
  );
}

function CategoriesDialog({ categories, onClose }: { categories: Category[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const save = useMutation({
    mutationFn: (x: { id: string | null; name: string }) => (x.id ? api.put(`/api/categories/${x.id}`, { name: x.name }) : api.post('/api/categories', { name: x.name })),
    onSuccess: () => {
      setName('');
      setEditId(null);
      void qc.invalidateQueries({ queryKey: ['categories'] });
    },
  });
  return (
    <Dialog open onClose={onClose} title="Categories" description="Group products for searching, reports and promotions.">
      <FormError error={save.error} />
      <form
        className="mb-4 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) save.mutate({ id: null, name });
        }}
      >
        <TextInput aria-label="New category" placeholder="New category name" value={name} onChange={(e) => setName(e.target.value)} />
        <Button type="submit" variant="primary" busy={save.isPending && !editId}>Add</Button>
      </form>
      <ul className="divide-y divide-line rounded-[8px] border border-line">
        {categories.map((c) => (
          <li key={c.id} className="flex items-center justify-between gap-3 px-3 py-2">
            {editId === c.id ? (
              <form
                className="flex flex-1 gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  save.mutate({ id: c.id, name: editName });
                }}
              >
                <TextInput aria-label="Category name" value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus />
                <Button size="sm" type="submit" variant="primary">Save</Button>
              </form>
            ) : (
              <>
                <span>
                  {c.name} <span className="text-sm text-muted">— {c.productCount} products</span>
                </span>
                <Button size="sm" variant="ghost" onClick={() => { setEditId(c.id); setEditName(c.name); }}>Rename</Button>
              </>
            )}
          </li>
        ))}
      </ul>
    </Dialog>
  );
}

function VariantGroupDialog({ categories, onClose, onCreated }: { categories: Category[]; onClose: () => void; onCreated: (p: ProductDto) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [v, setV] = useState({ name: '', sku: '', categoryId: '', unit: 'each', vatCategory: 'standard', attributes: 'Colour' });
  const set = <K extends keyof typeof v>(k: K, val: (typeof v)[K]) => setV((p) => ({ ...p, [k]: val }));
  const attrs = v.attributes.split(',').map((a) => a.trim()).filter(Boolean);
  const m = useMutation({
    mutationFn: () => api.post<ProductDto>('/api/product-groups', { ...v, categoryId: v.categoryId || null, attributes: attrs }),
    onSuccess: (p) => {
      toast(`${p.name} created. Now add its options.`);
      void qc.invalidateQueries({ queryKey: ['products'] });
      onCreated(p);
    },
  });
  const f = fieldErrors(m.error);
  return (
    <Dialog
      open
      onClose={onClose}
      title="Product with options"
      description="For one item sold in several versions, like a couch in three fabrics. Each version gets its own code, price and stock."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!v.name.trim() || !v.sku.trim() || !attrs.length} busy={m.isPending} onClick={() => m.mutate()}>Create</Button>
        </>
      }
    >
      <FormError error={m.error} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name" htmlFor="vg-name" error={f.name} className="sm:col-span-2">
          <TextInput id="vg-name" value={v.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Mazowe 3-seater couch" />
        </Field>
        <Field label="Code" htmlFor="vg-sku" error={f.sku} hint="Each option's code usually starts with this">
          <TextInput id="vg-sku" value={v.sku} onChange={(e) => set('sku', e.target.value.toUpperCase())} />
        </Field>
        <Field label="Category" htmlFor="vg-cat">
          <Select id="vg-cat" value={v.categoryId} onChange={(e) => set('categoryId', e.target.value)}>
            <option value="">None</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </Select>
        </Field>
        <Field label="Sold per" htmlFor="vg-unit">
          <Select id="vg-unit" value={v.unit} onChange={(e) => set('unit', e.target.value)}>
            {(['each', 'set', 'pair', 'pack', 'box'] as const).map((u) => <option key={u} value={u}>{UNITS[u].label}</option>)}
          </Select>
        </Field>
        <Field label="VAT" htmlFor="vg-vat">
          <Select id="vg-vat" value={v.vatCategory} onChange={(e) => set('vatCategory', e.target.value)}>
            {VAT_CATEGORY_KEYS.map((k) => <option key={k} value={k}>{VAT_CATEGORIES[k]}</option>)}
          </Select>
        </Field>
        <Field label="Options" htmlFor="vg-attr" error={f.attributes} className="sm:col-span-2" hint="What varies, separated by commas (up to three), e.g. Colour, Fabric">
          <TextInput id="vg-attr" value={v.attributes} onChange={(e) => set('attributes', e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

function VariantsDialog({ parent, branchId, onClose }: { parent: ProductDto; branchId?: string; onClose: () => void }) {
  const { me, can } = useSession();
  const cur = me.business.baseCurrency as CurrencyCode;
  const qc = useQueryClient();
  const toast = useToast();
  const options = Object.keys(parent.variantAttributes ?? {});
  const list = useQuery({
    queryKey: ['variants', parent.id, branchId],
    queryFn: () => api.get<ProductDto[]>(`/api/products/${parent.id}/variants${branchId ? `?branchId=${branchId}` : ''}`),
  });
  const blank = () => ({ values: Object.fromEntries(options.map((o) => [o, ''])) as Record<string, string>, sku: '', priceCents: null as number | null, costCents: null as number | null });
  const [v, setV] = useState(blank);
  const m = useMutation({
    mutationFn: () => api.post<ProductDto>(`/api/products/${parent.id}/variants`, { values: v.values, sku: v.sku, priceCents: v.priceCents, costCents: v.costCents ?? 0 }),
    onSuccess: (p) => {
      toast(`${p.name} added`);
      setV(blank());
      void qc.invalidateQueries({ queryKey: ['variants', parent.id] });
      void qc.invalidateQueries({ queryKey: ['products'] });
    },
  });
  const f = fieldErrors(m.error);
  const filled = options.every((o) => v.values[o]?.trim()) && v.sku.trim() && v.priceCents != null;
  return (
    <Dialog open wide onClose={onClose} title={parent.name} description={`${parent.sku} — options by ${options.join(' and ').toLowerCase()}. Sell, stock and receive each option on its own.`}>
      {list.isLoading && <Spinner />}
      {list.error && <ErrorNotice error={list.error} />}
      {list.data?.length === 0 && <p className="mb-4 text-muted">No options yet. Add the first one below.</p>}
      {!!list.data?.length && (
        <table className="mb-5 w-full text-left">
          <thead>
            <tr className="border-b border-line text-sm text-muted">
              {options.map((o) => <th key={o} className="py-2 pr-3 font-medium">{o}</th>)}
              <th className="px-3 py-2 font-medium">Code</th>
              <th className="px-3 py-2 text-right font-medium">Price</th>
              <th className="py-2 pl-3 text-right font-medium">In stock</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {list.data.map((c) => (
              <tr key={c.id}>
                {options.map((o) => <td key={o} className="py-2 pr-3">{c.variantAttributes?.[o] ?? '—'}</td>)}
                <td className="px-3 py-2 text-sm">{c.sku}</td>
                <td className="px-3 py-2 text-right num">{formatMoney(c.priceCents, cur)}</td>
                <td className={clsx('py-2 pl-3 text-right num', (c.stock?.qtyMilli ?? 0) <= 0 && 'text-danger')}>{formatQty(c.stock?.qtyMilli ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {can('variants.manage') && (
        <div className="rounded-[8px] border border-line p-4">
          <p className="mb-3 font-medium">Add an option</p>
          <FormError error={m.error} />
          <div className="grid gap-3 sm:grid-cols-3">
            {options.map((o) => (
              <Field key={o} label={o} htmlFor={`va-${o}`} error={f.values}>
                <TextInput id={`va-${o}`} value={v.values[o] ?? ''} onChange={(e) => setV((p) => ({ ...p, values: { ...p.values, [o]: e.target.value } }))} />
              </Field>
            ))}
            <Field label="Code" htmlFor="va-sku" error={f.sku}>
              <TextInput id="va-sku" value={v.sku} placeholder={`${parent.sku}-…`} onChange={(e) => setV((p) => ({ ...p, sku: e.target.value.toUpperCase() }))} />
            </Field>
            <Field label="Price" htmlFor="va-price" error={f.priceCents}>
              <MoneyInput id="va-price" currency={cur} value={v.priceCents} onChange={(c) => setV((p) => ({ ...p, priceCents: c }))} />
            </Field>
            {can('products.view_cost') && (
              <Field label="Cost (optional)" htmlFor="va-cost">
                <MoneyInput id="va-cost" currency={cur} value={v.costCents} onChange={(c) => setV((p) => ({ ...p, costCents: c }))} />
              </Field>
            )}
          </div>
          <div className="mt-3 flex justify-end">
            <Button variant="primary" disabled={!filled} busy={m.isPending} onClick={() => m.mutate()}>Add option</Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
